/**
 * First-touch inbound attribution — the client capture (contract A12,
 * `docs/prd-landing-pages.md` §7).
 *
 * **Same-visit, held in memory, and there is deliberately no device
 * storage. Read this before "fixing" the returning-visitor gap.**
 *
 * The obvious implementation writes the campaign to `localStorage` so a
 * parent who arrives from an ad on Monday and returns by search on Sunday
 * is still credited to Monday. It is **deliberately not done**, for a reason
 * that is legal rather than technical: keeping the value on the device for six
 * days and reading it back is storage of, and access to, information on
 * terminal equipment. Under PECR that needs consent or a specific exemption
 * *regardless of whether the stored value is personal data*, and attribution is
 * not strictly necessary to provide the service, so no exemption applies.
 *
 * DPIA P9 (legitimate interests, aggregate use only) is the basis for
 * **holding** attribution on the account. It does not cover the
 * device-storage leg — that is S2 in `docs/prd-landing-pages.md`, still
 * open. Collapsing the two is the easy mistake; they are
 * different processing with different bases. In a product whose cohort
 * skews under 18, unconsented terminal-equipment storage is the wrong
 * default to reach for.
 *
 * So: module memory. It survives the client-side landing → `/register`
 * navigation, which is the only hop that has to work, and dies on reload.
 * What is lost is the returning visitor, who is attributed to the visit
 * that registers. That undercounts channels with long consideration
 * cycles; §7 records it as a known bias in the report (§8) and explicitly
 * does not fix it with tracking. Do not fix it here either.
 *
 * **Nothing here is consent-gated.** Attribution rides on P9 legitimate
 * interests, a different lawful basis from the Art. 6(1)(a) consent that
 * gates Statsig, so it is captured and written for every account whatever
 * the consent toggles say. For the same reason — P9 is limited to
 * aggregate use, and individual-level targeting would need consent and
 * re-assessment (DPIA C4) — none of these values may become a
 * Statsig property or any other per-person attribute.
 *
 * **What is trusted, and what is not.** Only the five `utm_*` values come
 * from the query string. `variant_slug` and `landing_path` are derived
 * from the matched route, never from the query, because a query-supplied
 * variant is attacker-chosen and anyone with a link could corrupt the
 * report. `referrer` is reduced to an origin: a full referrer URL leaks
 * the path of the page someone came from, which can itself be personal
 * data, and aggregate attribution never needs it. `captured_at` is not
 * sent at all — the server stamps its own clock, because a client
 * timestamp is unverifiable and a fingerprinting surface for no gain.
 */

import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";

/** The five query-string parameters, and the only values taken from it. */
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

/**
 * The `attribution` object sent inside the registration request.
 *
 * Keys are byte-identical to the `account_attributions` columns, so a
 * reader of the table and a reader of this module share one vocabulary and
 * there is no renaming layer to get wrong. Every key is optional: the
 * server forbids unknown keys (422) and treats an absent object as the
 * normal direct-traffic case. Values are never `''` — an empty parameter
 * is dropped here rather than sent as an empty string.
 */
export interface AttributionPayload {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  variant_slug?: string;
  landing_path?: string;
  referrer?: string;
}

// The whole store. Module scope, not a ref: nothing renders it, and a
// reactive wrapper would only invite a component to display it.
let firstTouch: AttributionPayload | null = null;

/**
 * The inbound referrer's origin, or `undefined`.
 *
 * Our own origin is not an inbound referrer — a client-side hop from our
 * own landing page would otherwise record RevisionLab as the source of its
 * own traffic. An unparseable referrer is dropped rather than guessed at.
 */
function inboundReferrerOrigin(): string | undefined {
  const raw = typeof document === "undefined" ? "" : document.referrer;
  if (!raw) return undefined;
  try {
    const { origin } = new URL(raw);
    if (origin === window.location.origin) return undefined;
    return origin;
  } catch {
    return undefined;
  }
}

/** First non-empty value for a repeated query parameter, trimmed. */
function queryValue(
  query: RouteLocationNormalized["query"],
  key: string,
): string | undefined {
  const raw = query[key];
  const first = Array.isArray(raw)
    ? raw.find((v) => v !== null && v !== "")
    : raw;
  if (typeof first !== "string") return undefined;
  const trimmed = first.trim();
  return trimmed === "" ? undefined : trimmed;
}

/**
 * Record the first touch of this visit, if it is not already recorded.
 *
 * First touch means first: once set, this never overwrites. A visitor who
 * lands on a campaign variant and then navigates to `/register` keeps the
 * campaign, and `/register` does not become their landing page.
 *
 * Values are **not** truncated to the server's length caps. An over-long
 * campaign slug is rejected with a 422 rather than silently shortened,
 * because a truncated slug is a different campaign and quietly wrong data
 * is worse than a loud failure.
 */
export function captureFirstTouch(to: RouteLocationNormalized): void {
  if (firstTouch !== null) return;

  const captured: AttributionPayload = {};
  for (const key of UTM_KEYS) {
    const value = queryValue(to.query, key);
    if (value !== undefined) captured[key] = value;
  }

  // Derived from the matched route. `variantSlug` is route meta so a new
  // landing variant is a registry entry, not an edit to this module.
  const variantSlug = to.meta.variantSlug;
  if (typeof variantSlug === "string" && variantSlug !== "") {
    captured.variant_slug = variantSlug;
  }
  captured.landing_path = to.path;

  const referrer = inboundReferrerOrigin();
  if (referrer !== undefined) captured.referrer = referrer;

  firstTouch = captured;
}

/**
 * Forget the captured attribution (S3 — a browser context is not a
 * person).
 *
 * Called on navigation away from the marketing and registration surfaces,
 * and on sign-out, so one family's Sunday signup cannot inherit Monday's
 * student visit on a shared device.
 */
export function clearFirstTouch(): void {
  firstTouch = null;
}

/**
 * Take the captured attribution for the registration request, clearing it.
 *
 * Read-and-clear rather than read: the value is consumed at registration
 * (§7), and leaving it behind would attribute a second account created in
 * the same visit to the first one's campaign.
 *
 * Returns `undefined` when there is nothing to send — direct traffic that
 * never crossed a capture surface. That is the normal case and the server
 * treats the absent object as an `unknown` row.
 */
export function consumeFirstTouch(): AttributionPayload | undefined {
  const captured = firstTouch;
  firstTouch = null;
  if (captured === null || Object.keys(captured).length === 0) return undefined;
  return captured;
}

/**
 * Router guard: capture on a marketing or registration surface, forget
 * everywhere else, and strip the `utm_*` parameters from the visible URL.
 *
 * **Why strip.** Without it, a visitor who copies the address out of their
 * bar and shares it re-attributes whoever pastes it, so word-of-mouth
 * traffic is silently counted as campaign traffic. Stripping preserves
 * unrelated query state and the fragment, and re-capture is idempotent, so
 * the back button restoring the parameterised URL changes nothing.
 *
 * Returns a replacement location when there is something to strip;
 * otherwise `true`. The guard runs before `authGuard`, so a redirect here
 * simply re-enters the chain with a clean query.
 */
export function attributionGuard(
  to: RouteLocationNormalized,
): RouteLocationRaw | boolean {
  if (to.meta.attributionSurface !== true) {
    clearFirstTouch();
    return true;
  }

  captureFirstTouch(to);

  const stripped = { ...to.query };
  let hadUtm = false;
  for (const key of UTM_KEYS) {
    if (key in stripped) {
      delete stripped[key];
      hadUtm = true;
    }
  }
  if (!hadUtm) return true;
  return { path: to.path, query: stripped, hash: to.hash, replace: true };
}
